
import { createSlice } from '@reduxjs/toolkit'
import type { User } from '../../types/user.types'
import {fetchUsers, searchUsers, searchPlayers, updateUser, createUser, deleteUser} from "../thunks/userThunks.ts";

interface UsersState {
    users: User[]
    searchResults: User[]
    playerSearchResults: User[]
    playerSearchLoading: boolean
    selectedUser: User | null
    loading: boolean
    searchLoading: boolean
    error: string | null
    playerSearchError: string | null
    pagination: {
        page: number
        size: number
        totalPages: number
        totalElements: number
    }
}

const initialState: UsersState = {
    users: [],
    searchResults: [],
    playerSearchResults: [],
    playerSearchLoading: false,
    selectedUser: null,
    loading: false,
    searchLoading: false,
    error: null,
    playerSearchError: null,
    pagination: {
        page: 0,
        size: 20,
        totalPages: 0,
        totalElements: 0
    }
}

const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setSelectedUser: (state, action) => {
            state.selectedUser = action.payload
        },
        clearSearchResults: (state) => {
            state.searchResults = []
        },
        clearPlayerSearchResults: (state) => {
            state.playerSearchResults = []
        },
        clearError: (state) => {
            state.error = null
        }
    },
    extraReducers: (builder) => {
        // Fetch Users
        builder.addCase(fetchUsers.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(fetchUsers.fulfilled, (state, action) => {
            state.users = action.payload.content
            state.pagination = {
                page: action.payload.number,
                size: action.payload.size,
                totalPages: action.payload.totalPages,
                totalElements: action.payload.totalElements
            }
            state.loading = false
        })
        builder.addCase(fetchUsers.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        // Search Users (staff-only, MARKER+)
        builder.addCase(searchUsers.pending, (state) => {
            state.searchLoading = true
        })
        builder.addCase(searchUsers.fulfilled, (state, action) => {
            state.searchResults = action.payload
            state.searchLoading = false
        })
        builder.addCase(searchUsers.rejected, (state, action) => {
            state.searchLoading = false
            state.error = action.payload as string
        })

        // Search Players (all roles, USER+)
        builder.addCase(searchPlayers.pending, (state) => {
            state.playerSearchLoading = true
            state.playerSearchError = null
        })
        builder.addCase(searchPlayers.fulfilled, (state, action) => {
            state.playerSearchResults = action.payload
            state.playerSearchLoading = false
        })
        builder.addCase(searchPlayers.rejected, (state, action) => {
            state.playerSearchLoading = false
            state.playerSearchError = action.payload as string
        })

        // Update User
        builder.addCase(updateUser.fulfilled, (state, action) => {
            const { userId, data } = action.payload
            const index = state.users.findIndex(u => u.id === userId)
            if (index !== -1) {
                state.users[index] = { ...state.users[index], ...data }
            }
        })

        // Create User
        builder.addCase(createUser.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(createUser.fulfilled, (state, action) => {
            state.users.unshift(action.payload)
            state.pagination.totalElements += 1
            state.loading = false
        })
        builder.addCase(createUser.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(deleteUser.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(deleteUser.fulfilled, (state, action) => {
            state.users = state.users.filter(u => u.id !== action.payload)
            state.pagination.totalElements = Math.max(0, state.pagination.totalElements - 1)
            state.loading = false
        })
        builder.addCase(deleteUser.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })
    }
})

export const { setSelectedUser, clearSearchResults, clearPlayerSearchResults, clearError } = usersSlice.actions
export default usersSlice.reducer